import type {Charter} from '../model/charter';
import {ShiftBat, ShiftBatLocationRow, ShiftBatRowType} from '../model/shiftBat';
import {PrivateHireEvent, PrivateHireLocation, PrivateHireOrder, PrivateHireTask} from './optibus-client';
import dayjs from '../dayjs';
import { BusRoute, Trip } from '../model/busRoute';
import { last } from 'lodash';
import { Schedule } from '../model/schedule';
import { VehicleType } from '../model/vehicleType';

const convertTime = (seconds: number) => {
    return dayjs().startOf('day').add(seconds, 'seconds').format('HH:mm');
};

interface ConversionOptions {
    depotName: string;  // Required for Optibus private hire tasks
}

/**
 * Converts a Busable location to an Optibus PrivateHireLocation
 */
function convertLocation(location: any): PrivateHireLocation {
    console.log('[convertLocation] Input location:', location);
    const result = {
        lng: location?.lon ?? 0,
        lat: location?.lat ?? 0,
        name: location?.name || location?.stopName || '',
        address: location?.address || location?.description || ''
    };
    console.log('[convertLocation] Converted location:', result);
    return result;
}

function createPrivateHireEvents(duty: ShiftBat): PrivateHireEvent[] {
    console.log('[createPrivateHireEvents] Starting with duty:', duty);
    const events: PrivateHireEvent[] = [];
    const stopTimes = duty.getStopTimes();
    console.log('[createPrivateHireEvents] Got stop times:', stopTimes);
    
    // Get duty start and end times
    const dutyStartTime = duty.getStartTime();
    const dutyEndTime = duty.getEndTime();
    const serviceStartTime = stopTimes.length > 0 ? stopTimes[0].arriveSecs : null;
    const serviceEndTime = stopTimes.length > 0 ? stopTimes[stopTimes.length - 1].arriveSecs : null;
    
    // DUTY SIGN ON TIME
    if (duty.rows.length > 0 && serviceStartTime) {
        const signOn: PrivateHireEvent = {
            type: 'custom',
            startTime: convertTime(dutyStartTime),
            endTime: convertTime(serviceStartTime),
            startLocation: convertLocation(stopTimes[0]),
            endLocation: convertLocation(stopTimes[0]),
            distance: 0
        };
        // if duty starttime same as service starttime, skip
        if (dutyStartTime !== serviceStartTime) {
            events.push(signOn);
        }
        else {
            console.log('[createPrivateHireEvents] Skipping sign on event because duty starttime same as service starttime');
        }
    }
    
    stopTimes.forEach((stopTime: any, index: number) => {
        if(index === 0) {
            console.log('[createPrivateHireEvents] Skipping first stop time');
            return; // Skip the first stop time
        }
        const previousStop = stopTimes[index - 1];
        console.log('[createPrivateHireEvents] Processing stop', index, 'Previous stop:', previousStop, 'Current stop:', stopTime);
    
        const event: PrivateHireEvent = {
            type: 'private_hire',
            startTime: convertTime(previousStop.arriveSecs),
            endTime: convertTime(stopTime.arriveSecs),
            startLocation: convertLocation(previousStop),
            endLocation: convertLocation(stopTime),
            distance: (stopTime.distance - previousStop.distance) / 1000
        };
        console.log('[createPrivateHireEvents] Created event:', event);
        events.push(event);
    });
    
    // DUTY SIGN OFF TIME
    if (duty.rows.length > 0 && serviceEndTime) {
        const signOff: PrivateHireEvent = {
            type: 'custom',
            startTime: convertTime(serviceEndTime),
            endTime: convertTime(dutyEndTime),
            startLocation: convertLocation(stopTimes[stopTimes.length - 1]),
            endLocation: convertLocation(stopTimes[stopTimes.length - 1]),
            distance: 0
        };
        // if duty endtime same as service endtime, skip
        if (dutyEndTime !== serviceEndTime) {
            events.push(signOff);
        }
        else {
            console.log('[createPrivateHireEvents] Skipping sign off event because duty endtime same as service endtime');
        }
    }
    
    console.log('[createPrivateHireEvents] Final events array:', events);
    return events;
}

function createPrivateHireEventsFromTrip(trip: Trip): PrivateHireEvent[] {
    const events: PrivateHireEvent[] = [];
    console.log('[createPrivateHireEventsFromTrip] Starting with trip:', trip.stops);
    trip.stops.forEach((stop: any, index: number) => {
        if(index === 0) {
            return;
        }
        const previousStop = trip.stops[index - 1];
    
        const event: PrivateHireEvent = {
            type: 'private_hire',
            startTime: convertTime(previousStop.departSecs),
            endTime: convertTime(stop.arriveSecs),
            startLocation: convertLocation(previousStop),
            endLocation: convertLocation(stop),
            distance: (stop.distance - previousStop.distance) / 1000
        };

        console.log('[createPrivateHireEventsFromTrip] Extracted values:', event);

        events.push(event);
    });
    return events;
}

/**
 * Creates a PrivateHireTask from a charter duty
 */
function createPrivateHireTask(charter: Charter, depotName: string, schedulesById: Record<string, Schedule>, allVehicleTypesById: Record<string, VehicleType> = {}): PrivateHireTask[] {
    console.log('[createPrivateHireTask] Starting with charter:', charter);
    console.log('[createPrivateHireTask] Using depotName:', depotName);

    let tasks: PrivateHireTask[] = [];
    charter.duties.forEach((d: ShiftBat) => {
        const duty = d instanceof ShiftBat ? d : new ShiftBat(d);
        const events = createPrivateHireEvents(duty);

        // Build vehicle requirements per duty
        const vehicleTypeIds: string[] | undefined = (duty as any)?.vehicleTypeIds;
        const vehicleTypeQuantities: Record<string, number> | undefined = (duty as any)?.vehicleTypeQuantities;
        const legacyVehicleTypeId: string | undefined = (duty as any)?.vehicleTypeId;

        type VehicleSpec = { vehicleTypeId?: string; quantity: number };
        let vehicleSpecs: VehicleSpec[] = [];

        if (Array.isArray(vehicleTypeIds) && vehicleTypeIds.length > 0) {
            vehicleSpecs = vehicleTypeIds.map((id: string) => ({
                vehicleTypeId: id,
                quantity: Math.max(1, parseInt(String(vehicleTypeQuantities?.[id] ?? 1), 10))
            })).filter(spec => spec.quantity > 0);
        } else if (legacyVehicleTypeId) {
            vehicleSpecs = [{ vehicleTypeId: legacyVehicleTypeId, quantity: 1 }];
        } else {
            // Default: one unspecified vehicle if nothing provided
            vehicleSpecs = [{ quantity: 1 }];
        }
        const dates = duty.getRunningDates ? duty.getRunningDates(schedulesById, dayjs()) : [];
        console.log('>> [dates]:', dates);
        dates.forEach((date: any) => {
            const baseTaskId = charter.name + '-' + charter.bookingReference + '-' + duty.shiftBatNumber + '-' + date.format('MMM_D');
            const taskDate = date.format('YYYY-MM-DD');

            vehicleSpecs.forEach((spec) => {
                const typeSuffix = spec.vehicleTypeId ? ('-vt_' + spec.vehicleTypeId) : '';
                const labels = spec.vehicleTypeId ? [ 'vehicleType:' + spec.vehicleTypeId ] : undefined;

                const vehicleTypeName = spec.vehicleTypeId ? allVehicleTypesById[spec.vehicleTypeId]?.vehicleTypeName || '' : '';

                for (let i = 1; i <= spec.quantity; i++) {
                    const indexSuffix = spec.quantity > 1 ? ('-#' + i) : '';
                    tasks.push({
                        taskId: baseTaskId + typeSuffix + indexSuffix,
                        date: taskDate,
                        depotName,
                        note: `${charter.name} - ${vehicleTypeName}`,
                        events: [...events],
                        noVehicleNeeded: false,
                        ...(labels ? { labelCodes: labels } : {})
                    });
                }
            });
        });
    });

    console.log('[createPrivateHireTask] Created tasks:', tasks);
    return tasks;
}

/**
 * Converts a Busable Charter to an Optibus PrivateHireOrder
 */
export function charterToPrivateHire(charter: Charter, options: ConversionOptions, schedulesById: Record<string, Schedule>, allVehicleTypesById: Record<string, VehicleType>): PrivateHireOrder {
    console.log('[charterToPrivateHire] Starting with charter:', charter);
    console.log('[charterToPrivateHire] Using options:', options);

    if (!charter) {
        console.log('[charterToPrivateHire] Charter is nil, throwing error');
        throw new Error('Cannot convert null or undefined charter to Optibus private hire');
    }

    if(!charter.bookingReference) {
        console.log('[charterToPrivateHire] Charter has no booking reference, throwing error');
        throw new Error('Cannot convert charter to Optibus private hire with no booking reference');
    }
    const privateHires: PrivateHireTask[] = createPrivateHireTask(charter, options.depotName, schedulesById, allVehicleTypesById);
    console.log('[charterToPrivateHire] Created private hires:', privateHires);

    const order: PrivateHireOrder = {
        orderId: charter?.bookingReference,
        orderStatus: 'confirmed',
        privateHires: privateHires
    };
    console.log('[charterToPrivateHire] Final order:', order);
    return order;
}

/**
 * Batch converts multiple Busable Charters to Optibus PrivateHireOrders
 */
export function batchChartersToPrivateHires(charters: Charter[], options: ConversionOptions, schedulesById: Record<string, Schedule>, allVehicleTypesById: Record<string, VehicleType>): PrivateHireOrder[] {
    console.log('[batchChartersToPrivateHires] Starting with charters:', charters);
    console.log('[batchChartersToPrivateHires] Using options:', options);

    const filteredCharters = charters?.filter(charter => charter) ?? [];
    console.log('[batchChartersToPrivateHires] Filtered charters:', filteredCharters);

    const orders = filteredCharters.map(charter => {
        console.log('[batchChartersToPrivateHires] Processing charter:', charter);
        const order = charterToPrivateHire(charter, options, schedulesById, allVehicleTypesById);
        console.log('[batchChartersToPrivateHires] Created order:', order);
        return order;
    });

    console.log('[batchChartersToPrivateHires] Final orders:', orders);
    return orders;
}
